/**
 * This file contains a list of analytics events listed under `EVENTS` variable that map to the events in the spec.
 * Events are grouped by component under `EVENTS_MAP`.
 * Each event is defined so that it contains all the details that the analytics events will need. There is a getter for each individual event.
 * The event details that have `null` are fields that are set dynamically in code. They are listed to let us know what are all the fields we need to set for a specific event.
 *
 * Resources:
 * spec @see @link {https://nerdwallet.atlassian.net/wiki/spaces/DATA/pages/1002045465/CC+Card+Details+Spec}
 * For subclass definitions and enums: @see @link {https://github.com/NerdWallet/app-schemata/tree/master/analytic_pipeline/credit_cards}
 */
import _ from 'lodash';

import appConfig from '@nerdwallet/app-config';
/**
 * @constant {Object}
 * @desc List of CTA modes
 */
export const CTA_MODE = {
  MODAL: 'modal',
  LINK: 'link',
};

/**
 * @constant {Object}
 * @desc List of CTA formats
 */
export const CTA_FORMAT = {
  BUTTON: 'button',
  LINK: 'link',
};

/**
 * @constant {Object}
 * @desc List of Auth types
 */
export const AUTH_TYPE = {
  REGISTER: 'register',
};

/**
 * @constant {Object}
 * @desc List of CTA labels
 */
export const CTA_LABEL = {
  GET_YOUR_CREDIT_SCORE: 'get_your_credit_score',
  SIGN_IN: 'sign_in',
  JOIN_NERDWALLET: 'join_nerdwallet',
  CHECK_YOUR_CREDIT_SCORE: 'check_your_credit_score',
  CHECK_YOUR_CREDIT_SCORE_FOR_FREE: 'check_your_credit_score_for_free',
};

/**
 * @constant {Object}
 * @desc List of TARGET_FLOW
 */
export const TARGET_FLOW = {
  'CREDIT-SCORE': 'credit-score',
  'ONBOARDING-GENERIC': 'onboarding-generic',
};

/**
 * @constant {Object}
 * @desc List of entity names used in analytics events
 */
export const ENTITY_NAME = {
  CITY_LIFE_CITY_SEARCH: 'mrt_city_life_search_city',
  CITY_LIFE_COMPARE_FIELD: 'mrt_city_life_compare_field',
  COST_OF_LIVING: 'mrt_cost_of_living',
  COST_OF_LIVING_RELATED_ARTICLES: 'cost_of_living_related_articles',
  HELP_BUBBLE_INTERACTED: 'help_bubble_interacted',
  MORTGAGES_CALCULATOR_REGISTRATION: 'mortgages_calculator_registration',
  MORTGAGES_HOME_AFFORDABILITY_REGISTRATION:
    'mortgages_home_affordability_registration',
  MORTGAGES_HOME_AFFORDABILITY_CALCULATOR:
    'mortgages_home_affordability_calculator',
  MORTGAGES_HOME_AFFORDABILITY_CALCULATOR_FORM:
    'mortgages_home_affordability_calculator_form',
  MRT_AMORTIZATION_CALCULATOR: 'mrt_amortization_calculator',
  MRT_AMORTIZATION_MONTHLY_PAYMENT: 'mrt_amortization_monthly_payment',
  MRT_STATE_MONTHLY_PAYMENT: 'mortgages_state_calculator_card_monthly_payment',
  MRT_STATE_CARD_COMPARE_LOAN_TYPES:
    'mortgages_state_calculator_card_compare_loan_types',
  MRT_STATE_CARD_AMORTIZATION: 'mortgages_state_calculator_card_amortization',
  MRT_PAYMENT_CALCULATOR: 'mrt_payment_calculator',
  MRT_CALCULATOR_MONTHLY_PAYMENT: 'mrt_calculator_monthly_payment',
  MRT_CALCULATOR_LATEST_RATES: 'mrt_calculator_latest_rates',
  MRT_CALCULATOR: 'mrt_calculator',
  MORTGAGES_REFI_CALCULATOR_DETAILS_OPEN:
    'mortgages_refi_calculator_details_open',
  MORTGAGES_REFI_CALCULATOR_SUMMARY: 'mortgages_refi_calculator_summary',
  MORTGAGES_REFI_CALCULATOR_SUMMARY_DETAILS:
    'mortgages_refi_calculator_summary_details',
  MRT_HOME_AFFORDABILITY: 'mrt_home_affordability',
  MRT_REFI_CALCULATOR: 'mrt_refi_calculator',
  MRT_REFI_CALCULATOR_DETAILS: 'mrt_refi_calculator_details',
  MRT_REFI_CALCULATOR_NEW_MORTGAGE: 'mrt_refi_calculator_new_mortgage',
  MRT_REFI_CALCULATOR_ORIGINAL_MORTGAGE:
    'mrt_refi_calculator_original_mortgage',
  HELP_BUTTON_INTERACTED: 'help_button_interacted',
  MORTGAGES_STATE_CALCULATOR: 'mortgages_state_calculator',
  MORTGAGES_STATE_CALCULATOR_OFFER: 'mortgages_calculator_offer',
  RENT_VS_BUY_CALCULATOR: 'mortgages_rent_vs_buy_calculator',
};

/**
 * @constant {Object}
 * @desc List of driverLocation names used in Segment registration events
 */
export const REG_DRIVER_LOCATION = {
  ONBOARDING_GET_CREDIT_SCORE: 'onboarding_get_credit_score',
  TOOL_CARD: 'tool_card',
};

/**
 * @constant {Object}
 * @desc Different event types to set event_type in analytics events
 */
export const EVENT_TYPE = {
  CREDIT_FLEXIBILITY_UPDATED: 'credit_flexibility_updated',
  CREDIT_SCORE_BAND_UPDATED: 'credit_score_band_updated',
  ELEMENT_INTERACTED: 'element_interacted',
  ELEMENT_IMPRESSION: 'element_impression',
  FORM_IMPRESSION: 'form_impression',
  GEO_PAGE_IMPRESSION: 'geo_page_impression',
  HELP_BUBBLE_INTERACTED: 'help_bubble_interacted',
  HOMEOWNER_INSURANCE_UPDATED: 'homeowner_insurance_updated',
  HOME_ASSOCIATION_FEE_UPDATED: 'home_association_fee_updated',
  HOME_CLOSING_COST_UPDATED: 'home_closing_cost_updated',
  HOME_CREDIT_SCORE_BAND_UPDATED: 'home_credit_score_band_updated',
  HOME_DOWN_PAYMENT_UPDATED: 'home_down_payment_updated',
  FORM_INPUT_CHANGED: 'form_input_changed',
  HOME_RENOVATION_COST_UPDATED: 'home_renovation_cost_updated',
  HOME_RENOVATION_FINANCING_TERM_UPDATED:
    'home_renovation_financing_term_updated',
  HOME_VALUE_UPDATED: 'home_value_updated',
  HOME_RENT_UPDATED: 'home_rent_updated',
  HOME_REPAIR_COST_PERCENT_UPDATED: 'home_repair_cost_percent_updated',
  HOME_RENT_SECURITY_DEPOSIT_MONTH_UPDATEDL:
    'home_rent_security_deposit_month_updated',
  HOME_RENTER_INSURANCE_PERCENT_UPDATED:
    'home_renter_insurance_percent_updated',
  HOME_APPRECIATION_PERCENT_UPDATED: 'home_appreciation_percent_updated',
  INCOME_PRETAX_INCOME_UPDATED: 'income_pretax_income_updated',
  INCOME_TAX_PERCENT_UPDATED: 'income_tax_percent_updated',
  MONTHLY_OBLIGATIONS_UPDATED: 'monthly_obligations_updated',
  MORTGAGE_BALANCE_UPDATED: 'mortgage_balance_updated',
  MORTGAGE_CASH_OUT_AMOUNT_UPDATED: 'mortgage_cash_out_amount_updated',
  MORTGAGE_INTEREST_RATE_APR_UPDATED: 'mortgage_interest_rate_apr_updated',
  MORTGAGE_LOAN_START_DATE_UPDATED: 'mortgage_loan_start_date_updated',
  MORTGAGE_ORIGINAL_AMOUNT_UPDATED: 'mortgage_original_amount_updated',
  MORTGAGE_ORIGINATION_YEAR_UPDATED: 'mortgage_origination_year_updated',
  MORTGAGE_PROPERTY_TAX_UPDATED: 'mortgage_property_tax_updated',
  MORTGAGE_RATE_COMPARE_UPDATED: 'mortgage_rate_compare_updated',
  MORTGAGE_REFINANCE_PURPOSE_UPDATED: 'mortgage_refinance_purpose_updated',
  MORTGAGE_TERM_UPDATED: 'mortgage_term_updated',
  MORTGAGE_DOWN_PAYMENT_PERCENT_UPDATED:
    'mortgage_down_payment_percent_updated',
  REGISTRATION_CTA_IMPRESSION: 'registration_cta_impression',
  US_LOCATION_UPDATED: 'us_location_updated',
  VETERAN_STATUS_UPDATED: 'veteran_status_updated',
  MONTHLY_DEBT_UPDATED: 'monthly_debt_updated',
  GENERAL_INFLATION_PERCENT_UPDATED: 'general_inflation_percent_updated',
  ANNUAL_RETURN_PERCENT_UPDATED: 'annual_return_percent_updated',
  TAX_FILING_STATUS_UPDATED: 'tax_filing_status_updated',
  CAPITAL_GAINS_TAX_RATE_UPDATED: 'capital_gains_tax_rate_updated',
};

/**
 * @constant {Object}
 * @desc Components by event types
 */
export const COMPONENTS = {
  HAC: 'hac',
  COST_OF_LIVING: 'cost_of_living',
  CITY_LIFE: 'city_life',
  MORTGAGE_AMORTIZATION: 'mortgage_amortization',
  REFINANCE_CALCULATOR: 'refinance_calculator',
  MORTGAGE_PAYMENT_CALCULATOR: 'mortgage_payment_calculator',
  RENT_VS_BUY_TOOL_CAL: 'rent_vs_buy_tool_cal',
  MORTGAGE_CALCULATOR: 'mortgage_calculator',
  MORTGAGE_STATE_PAYMENT_CALCULATOR: 'mortgage_state_payment_calculator',
};

/**
 * @const String
 * @desc form_submitted and form_cancelled
 */
export const FORM_SUBMITTED = 'form_submitted';
export const FORM_CANCELLED = 'form_cancelled';

/**
 * @constant {Object}
 * @desc Obstacle Types
 */
export const OBSTACLE_TYPE = {
  BAD_USER_INPUT: 'bad_user_input',
  DATA_PROVIDER_FAILED: 'data_provider_failed',
  UNEXPECTED_SYSTEM_ERROR: 'unexpected_system_error',
};

/**
 * @constant {Object}
 * @desc Enum of Mortgage types
 */
export const MORTGAGE_TYPE = {
  ORIGINAL: 'ORIGINAL',
  NEW: 'NEW',
};

/**
 * @constant {Object}
 * @desc Array of obstacle types used in the client
 */
export const OBSTACLE_TYPE_LIST = [
  OBSTACLE_TYPE.BAD_USER_INPUT,
  OBSTACLE_TYPE.DATA_PROVIDER_FAILED,
  OBSTACLE_TYPE.UNEXPECTED_SYSTEM_ERROR,
];

/**
 * @constant {Object}
 * @desc List of Form names
 */
export const FORM_NAME = {
  CITY_LIFE_CITY_SEARCH: 'mrt_city_life_search_city',
  CITY_LIFE_COMPARE_FIELD: 'mrt_city_life_compare_field',
  COST_OF_LIVING: 'mrt_cost_of_living',
  HOME_AFFORDABILITY: 'mrt_home_affordability',
  REFI_CALCULATOR: 'mrt_refi_calculator',
  REFI_CALCULATOR_DETAILS: 'mrt_refi_calculator_details',
  REFI_CALCULATOR_ORIGINAL_MORTGAGE: 'mrt_refi_calculator_original_mortgage',
  REFI_CALCULATOR_NEW_MORTGAGE: 'mrt_refi_calculator_new_mortgage',
  MRT_AMORTIZATION_CALCULATOR: 'mrt_amortization_calculator',
  MRT_CALCULATOR: 'mrt_calculator',
  MRT_CALCULATOR_LATEST_RATES: 'mrt_calculator_latest_rates',
  MRT_HOME_AFFORDABILITY_UPDATE: 'mrt_home_affordability_update',
  RENT_VS_BUY_CALCULATOR: 'mrt_rent_vs_buy',
};

/**
 * @constant {Object}
 * @desc List of Section names
 */
export const SECTION_NAME = {
  CITY_LIFE_COMPARE_CITY: 'city_life_compare_city',
  CITY_LIFE_CITY_SEARCH: 'mrt_city_life_search_city',
  COST_OF_LIVING: 'mrt_cost_of_living',
  COST_OF_LIVING_CARD: 'cost_of_living_tool_card',
  COST_OF_LIVING_RELATED_ARTICLES: 'cost_of_living_related_articles',
  MRT_HOME_AFFORDABILITY_UPDATE: 'mrt_home_affordability_update',
  MRT_HOME_IMPROVEMENT_LOAN: 'mrt_home_improvement_loan',
  MORTGAGES_HOME_AFFORDABILITY_NAVIGATION_BAR:
    'mortgages_home_affordability_navigation_bar',
  MRT_HOME_AFFORDABILITY: 'mrt_home_affordability',
  MRT_AMORTIZATION_CALCULATOR: 'mrt_amortization_calculator',
  COST_OF_LIVING_FREE_CREDIT_SCORE: 'cost_of_living_free_credit_score',
  CITY_LIFE_FREE_CREDIT_SCORE: 'city_life_free_credit_score',
  MORTGAGES_HOME_BUYING_GUIDE: 'mortgages_home_buying_guide',
  CITY_LIFE_TOOL_CARD: 'city_life_tool_card',
  COST_OF_LIVING_TOOL_CARD: 'cost_of_living_tool_card',
  MORTGAGES_HOME_AFFORDABILITY_TOOL_CARD:
    'mortgages_home_affordability_tool_card',
  MORTGAGES_REFINANCE_TOOL_CARD: 'mortgages_refinance_tool_card',
  MORTGAGES_REFI_CALCULATOR_SUMMARY: 'mortgages_refi_calculator_summary',
  MORTGAGES_CALCULATOR_TOOL_CARD: 'mortgages_calculator_tool_card',
  MRT_STATE_CALCULATOR_CARD: 'mortgages_state_calculator_card',
  RENT_VS_BUY_TOOL_CARD: 'rent_vs_buy_tool_card',
  MORTGAGES_HOME_AFFORDABILITY_METHODOLOGY:
    'mortgages_home_affordability_methodology',
  MORTGAGES_STATE_CALCULATOR_TOOL_CARD: 'mortgages_state_calculator_tool_card',
  MORTGAGES_PAYMENT_CALCULATOR_TOOL_CARD:
    'mortgages_payment_calculator_tool_card',
  MORTGAGES_AMORTIZATION_TOOL_CARD: 'mortgages_amortization_tool_card',
  MRT_REFI_CALCULATOR_DETAILS: 'mrt_refi_calculator_details',
  MRT_CALCULATOR_MONTHLY_PAYMENT: 'mrt_calculator_monthly_payment',
  MORTGAGES_STATE_CALCULATOR_OFFER: 'mortgages_calculator_offer',
};

/**
 * @constant {Object}
 * @desc List of Field names
 */
export const FIELD_NAME = {
  REFI_CALC: {
    CONTROL_PANEL: {
      TERM: 'refiterm_controlpanel_increment',
      RATE: 'refirate_controlpanel_increment',
      CASHOUT: 'cashout_controlpanel_input',
      CLOSINGCOST: 'closingcost_controlpanel_input',
      PRINCIPAL: 'principal_controlpanel_input',
      LOANTERM: 'loanterm_controlpanel_increment',
      INTEREST: 'interest_controlpanel_increment',
      ORIGNALYEAR: 'origyear_controlpanel_input',
    },
  },
};

/**
 * @constant {Object}
 * @desc List of INPUT TYPES
 */
export const INPUT_TYPE = {
  PERCENTAGE: 'percentage',
  INPUT: 'input',
  SELECT: 'select',
  STEPPER: 'stepper',
};

/**
 * @constant {Object}
 * @desc All the HAC events used in this client
 */
export const HAC_EVENTS = {
  GET_CREDIT_SCORE: 'get_credit_score',
  JOIN_NERDWALLET: 'join_nerdwallet',
  CHECK_CREDIT_SCORE: 'check_credit_score',
  GET_YOUR_FREE_CREDIT_SCORE: 'get_your_free_credit_score',
  CHECK_YOUR_CREDIT_SCORE_FOR_FREE: 'check_your_credit_score_for_free',
};

/**
 * @constant {Object}
 * @desc All the Refinance calculator events used in this client
 */
export const REFINANCE_CAL_EVENTS = {
  GET_YOUR_FREE_CREDIT_SCORE: 'get_your_free_credit_score',
  CALCULATOR_SUMMARY: 'calculator_summary',
};

/**
 * @constant {Object}
 * @desc All the Mortgage payment calculator events used in this client
 */
export const MORTGAGE_CALCULATOR_EVENTS = {
  GET_YOUR_FREE_CREDIT_SCORE: 'get_your_free_credit_score',
};

/**
 * @constant {Object}
 * @desc All the COST_OF_LIVING_EVENTS events used in this client
 */
export const COST_OF_LIVING_EVENTS = {
  JOIN_NERDWALLET: 'join_nerdwallet',
  GET_YOUR_FREE_CREDIT_SCORE: 'get_your_free_credit_score',
};

/**
 * @constant {Object}
 * @desc All the CITY_LIFE_EVENTS events used in this client
 */
export const CITY_LIFE_EVENTS = {
  JOIN_NERDWALLET: 'join_nerdwallet',
  GET_YOUR_FREE_CREDIT_SCORE: 'get_your_free_credit_score',
};

/**
 * @constant {Object}
 * @desc All the MORTGAGE_AMORTIZATION_EVENTS events used in this client
 */
export const MORTGAGE_AMORTIZATION_EVENTS = {
  JOIN_NERDWALLET: 'join_nerdwallet',
  GET_YOUR_FREE_CREDIT_SCORE: 'get_your_free_credit_score',
};

/**
 * @constant {Object}
 * @desc All the RENT_VS_BUY_TOOL_CAL_EVENTS events used in this client
 */
export const RENT_VS_BUY_TOOL_CAL_EVENTS = {
  GET_YOUR_FREE_CREDIT_SCORE: 'get_your_free_credit_score',
};

/**
 * @constant {Object}
 * @desc HAC event payload
 */
export const hacEventPayload = {
  eventType: EVENT_TYPE.REGISTRATION_CTA_IMPRESSION,
  entityName: ENTITY_NAME.MORTGAGES_HOME_AFFORDABILITY_REGISTRATION,
};

export const mortgagesCalToolCardPayload = {
  eventType: EVENT_TYPE.REGISTRATION_CTA_IMPRESSION,
  entityName: ENTITY_NAME.MORTGAGES_CALCULATOR_REGISTRATION,
};

/**
 * @constant {Object}
 * @desc HAC event additional attributes list
 */
export const hacEventPayloadAttributes = {
  target_flow: TARGET_FLOW['CREDIT-SCORE'],
  cta_mode: CTA_MODE.MODAL,
  cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
  cta_format: CTA_FORMAT.BUTTON,
  auth_type: AUTH_TYPE.REGISTER,
};

/**
 * @constant {Object}
 * @desc HAC event payload attributes list
 */
export const payloadAttributes = {
  element_position: null,
  section_position: null,
};

/**
 * @constant {Object}
 * @desc Cost of Living event payload
 */
export const costOfLivingPayload = {
  eventType: EVENT_TYPE.REGISTRATION_CTA_IMPRESSION,
  entityName: ENTITY_NAME.MORTGAGES_CALCULATOR_REGISTRATION,
};

/**
 * @constant {Object}
 * @desc Mortgage Amortization event payload
 */
export const mortgageAmortizationPayload = {
  eventType: EVENT_TYPE.REGISTRATION_CTA_IMPRESSION,
  entityName: ENTITY_NAME.MORTGAGES_CALCULATOR_REGISTRATION,
};

/**
 * @constant {Object}
 * @desc All the events related to the Cost of Living Calculator page
 */
export const costOfLivingEvents = {
  [COST_OF_LIVING_EVENTS.JOIN_NERDWALLET]: {
    ...costOfLivingPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_position: 2,
      section_name: SECTION_NAME.COST_OF_LIVING_FREE_CREDIT_SCORE,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        target_flow: TARGET_FLOW['ONBOARDING-GENERIC'],
        cta_label: CTA_LABEL.JOIN_NERDWALLET,
      },
    },
  },
  [COST_OF_LIVING_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...costOfLivingPayload,
    payload: {
      ...payloadAttributes,
      section_position: 6,
      section_name: SECTION_NAME.COST_OF_LIVING_TOOL_CARD,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the city life calculator
 */
export const cityLifeEvents = {
  [CITY_LIFE_EVENTS.JOIN_NERDWALLET]: {
    ...costOfLivingPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_position: 6,
      section_name: SECTION_NAME.CITY_LIFE_FREE_CREDIT_SCORE,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        target_flow: TARGET_FLOW['ONBOARDING-GENERIC'],
        cta_label: CTA_LABEL.JOIN_NERDWALLET,
      },
    },
  },
  [CITY_LIFE_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...mortgagesCalToolCardPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_position: 10,
      section_name: SECTION_NAME.CITY_LIFE_TOOL_CARD,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the HAC calculator page
 */
export const hacEvents = {
  [HAC_EVENTS.GET_CREDIT_SCORE]: {
    ...hacEventPayload,
    payload: {
      section_name: SECTION_NAME.MRT_HOME_AFFORDABILITY,
      ...payloadAttributes,
      additional_attributes: {
        ...hacEventPayloadAttributes,
      },
    },
  },
  [HAC_EVENTS.CHECK_CREDIT_SCORE]: {
    ...hacEventPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_name: SECTION_NAME.MORTGAGES_HOME_AFFORDABILITY_NAVIGATION_BAR,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.CHECK_YOUR_CREDIT_SCORE,
      },
    },
  },
  [HAC_EVENTS.JOIN_NERDWALLET]: {
    ...hacEventPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_name: SECTION_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        target_flow: TARGET_FLOW['ONBOARDING-GENERIC'],
        cta_label: CTA_LABEL.JOIN_NERDWALLET,
      },
    },
  },
  [HAC_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...hacEventPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_position: 6,
      section_name: SECTION_NAME.MORTGAGES_HOME_AFFORDABILITY_TOOL_CARD,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
  [HAC_EVENTS.CHECK_YOUR_CREDIT_SCORE_FOR_FREE]: {
    ...hacEventPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_name: SECTION_NAME.MORTGAGES_HOME_AFFORDABILITY_METHODOLOGY,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.CHECK_YOUR_CREDIT_SCORE_FOR_FREE,
        cta_format: CTA_FORMAT.LINK,
      },
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the Refinance calculator page
 */
export const refinanceCalEvents = {
  [REFINANCE_CAL_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...mortgagesCalToolCardPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_position: 7,
      section_name: SECTION_NAME.MORTGAGES_REFINANCE_TOOL_CARD,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
  [REFINANCE_CAL_EVENTS.CALCULATOR_SUMMARY]: {
    eventType: EVENT_TYPE.ELEMENT_IMPRESSION,
    entityName: ENTITY_NAME.MORTGAGES_REFI_CALCULATOR_SUMMARY,
    payload: {
      element_position: null,
      section_name: SECTION_NAME.MORTGAGES_REFI_CALCULATOR_SUMMARY,
      section_position: 1, // update if details-table position changes on page
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the Rent vs Buy calculator page
 */
export const rentVsBuyEvents = {
  [RENT_VS_BUY_TOOL_CAL_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...mortgagesCalToolCardPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_position: 4,
      section_name: SECTION_NAME.RENT_VS_BUY_TOOL_CARD,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the Mortgage Payment calculator page
 */
export const mortgageCalEvents = {
  [MORTGAGE_CALCULATOR_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...mortgagesCalToolCardPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_name: SECTION_NAME.MORTGAGES_CALCULATOR_TOOL_CARD,
      section_position: 6,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the Mortgage Payment calculator page
 */
export const mortgageStateCalEvents = {
  [MORTGAGE_CALCULATOR_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...mortgagesCalToolCardPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_name: SECTION_NAME.MORTGAGES_STATE_CALCULATOR_TOOL_CARD,
      section_position: 8,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the Mortgage Payment calculator page
 */
export const mortgagePaymentCalEvents = {
  [MORTGAGE_CALCULATOR_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...mortgagesCalToolCardPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_name: SECTION_NAME.MORTGAGES_PAYMENT_CALCULATOR_TOOL_CARD,
      section_position: 6,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
};

/**
 * @constant {Object}
 * @desc All the events related to the Mortgage Amortization calculator
 */
export const mortgageAmortizationEvents = {
  [MORTGAGE_AMORTIZATION_EVENTS.JOIN_NERDWALLET]: {
    ...mortgageAmortizationPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_position: 3,
      section_name: SECTION_NAME.MORTGAGES_HOME_BUYING_GUIDE,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        target_flow: TARGET_FLOW['ONBOARDING-GENERIC'],
        cta_label: CTA_LABEL.JOIN_NERDWALLET,
      },
    },
  },
  [MORTGAGE_AMORTIZATION_EVENTS.GET_YOUR_FREE_CREDIT_SCORE]: {
    ...mortgagesCalToolCardPayload,
    obstacle_type: OBSTACLE_TYPE_LIST,
    payload: {
      ...payloadAttributes,
      section_name: SECTION_NAME.MORTGAGES_AMORTIZATION_TOOL_CARD,
      section_position: 7,
      additional_attributes: {
        ...hacEventPayloadAttributes,
        cta_label: CTA_LABEL.GET_YOUR_CREDIT_SCORE,
      },
    },
  },
};

export const EVENTS_MAP = {
  [COMPONENTS.HAC]: hacEvents,
  [COMPONENTS.COST_OF_LIVING]: costOfLivingEvents,
  [COMPONENTS.CITY_LIFE]: cityLifeEvents,
  [COMPONENTS.MORTGAGE_AMORTIZATION]: mortgageAmortizationEvents,
  [COMPONENTS.REFINANCE_CALCULATOR]: refinanceCalEvents,
  [COMPONENTS.MORTGAGE_PAYMENT_CALCULATOR]: mortgagePaymentCalEvents,
  [COMPONENTS.RENT_VS_BUY_TOOL_CAL]: rentVsBuyEvents,
  [COMPONENTS.MORTGAGE_CALCULATOR]: mortgageCalEvents,
  [COMPONENTS.MORTGAGE_STATE_PAYMENT_CALCULATOR]: mortgageStateCalEvents,
};

/**
 * Getter function to fetch the details property of the given event object.
 * @function
 * @param {Object} event
 * @returns {Object}
 */
export const getEventDetails = (event) => _.get(event, 'payload');

/**
 * Getter function to fetch the entityName property of the given event object.
 * @function
 * @param {Object} event
 * @returns {string}
 */
export const getEventEntityName = (event) => _.get(event, 'entityName');

/**
 * Getter function to fetch the eventType property of the given event object.
 * @function
 * @param {Object} event
 * @returns {string}
 */
export const getEventType = (event) => _.get(event, 'eventType');

/**
 * Getter function to fetch the component object associated with the given component name,
 * using the EVENTS_MAP.
 * @function
 * @param {string} component - the component name
 * @returns {Object}
 */
export const getEvents = (component) => _.get(EVENTS_MAP, component);

/**
 * Getter function to fetch the GET_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getCSOnboarding = () =>
  _.get(getEvents(COMPONENTS.HAC), HAC_EVENTS.GET_CREDIT_SCORE);

/**
 * Getter function to fetch the JOIN_NERDWALLET event props.
 * @function
 * @returns {Object}
 */
export const getJoinNerdwallet = () =>
  _.get(getEvents(COMPONENTS.HAC), HAC_EVENTS.JOIN_NERDWALLET);

/**
 * Getter function to fetch the HAC_EVENTS.GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getHACFreeCreditScore = () =>
  _.get(getEvents(COMPONENTS.HAC), HAC_EVENTS.GET_YOUR_FREE_CREDIT_SCORE);

/**
 * Getter function to fetch the HAC_EVENTS.CHECK_YOUR_CREDIT_SCORE_FOR_FREE event props.
 * @function
 * @returns {Object}
 */
export const getHACCheckFreeCreditScore = () =>
  _.get(getEvents(COMPONENTS.HAC), HAC_EVENTS.CHECK_YOUR_CREDIT_SCORE_FOR_FREE);

/**
 * Getter function to fetch the CHECK_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getCheckCreditScore = () =>
  _.get(getEvents(COMPONENTS.HAC), HAC_EVENTS.CHECK_CREDIT_SCORE);

/**
 * Getter function to fetch the COST_OF_LIVING_EVENTS.JOIN_NERDWALLET event props.
 * @function
 * @returns {Object}
 */
export const getColJoinNerdwallet = () =>
  _.get(
    getEvents(COMPONENTS.COST_OF_LIVING),
    COST_OF_LIVING_EVENTS.JOIN_NERDWALLET
  );

/**
 * Getter function to fetch the COST_OF_LIVING_EVENTS.GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getColFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.COST_OF_LIVING),
    COST_OF_LIVING_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

/**
 * Getter function to fetch the CITY_LIFE_EVENTS.JOIN_NERDWALLET event props.
 * @function
 * @returns {Object}
 */
export const getCityLifeJoinNerdwallet = () =>
  _.get(getEvents(COMPONENTS.CITY_LIFE), CITY_LIFE_EVENTS.JOIN_NERDWALLET);

/**
 * Getter function to fetch the CITY_LIFE_EVENTS.GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getCityLifeFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.CITY_LIFE),
    CITY_LIFE_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

/**
 * Getter function to fetch the MORTGAGE_AMORTIZATION_EVENTS.JOIN_NERDWALLET event props.
 * @function
 * @returns {Object}
 */
export const getMortgageAmortizationJoinNW = () =>
  _.get(
    getEvents(COMPONENTS.MORTGAGE_AMORTIZATION),
    MORTGAGE_AMORTIZATION_EVENTS.JOIN_NERDWALLET
  );

/**
 * Getter function to fetch the GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getRefinanceFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.REFINANCE_CALCULATOR),
    REFINANCE_CAL_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

/**
 * Getter function to fetch the GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getMortgageAmortizationFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.MORTGAGE_AMORTIZATION),
    MORTGAGE_AMORTIZATION_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

/**
 * Getter function to fetch the GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getMortgagePaymentFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.MORTGAGE_PAYMENT_CALCULATOR),
    MORTGAGE_CALCULATOR_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

/**
 * Getter function to fetch the GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getMortgageStateFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.MORTGAGE_STATE_PAYMENT_CALCULATOR),
    MORTGAGE_CALCULATOR_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

/**
 * Getter function to fetch the GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getMortgageFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.MORTGAGE_CALCULATOR),
    MORTGAGE_CALCULATOR_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

/**
 * Getter function to fetch the GET_YOUR_FREE_CREDIT_SCORE event props.
 * @function
 * @returns {Object}
 */
export const getRentVsBuyFreeCreditScore = () =>
  _.get(
    getEvents(COMPONENTS.RENT_VS_BUY_TOOL_CAL),
    RENT_VS_BUY_TOOL_CAL_EVENTS.GET_YOUR_FREE_CREDIT_SCORE
  );

export const HOME_LIGHT_IMPRESSION_PAYLOAD = {
  destination_url: `${appConfig.SITE_BASE_URL}/redirect/eeadcaf6-38db-11eb-a727-ef6ff84e35fe`,
  marketplace_product_type: 'mortgages',
  marketplace_offer_id: 'eeadcaf6-38db-11eb-a727-ef6ff84e35fe',
  marketplace_product_id: 'bc922eae-38db-11eb-b58d-d3893be64b0d',
  marketplace_institution_id: '8ecec748-38db-11eb-b58d-3f5d38533912',
  monetizable: 'yes_assumed',
};

export const HOME_LIGHT_IMPRESSION = {
  eventType: 'product_impression',
  entityName: 'mortgages_calculator_agent',
  payload: HOME_LIGHT_IMPRESSION_PAYLOAD,
};

/**
 * Getter function to fetch the CALCULATOR_SUMMARY event props.
 * @function
 * @returns {Object}
 */
export const getRefinanceCalculatorSummary = () =>
  _.get(
    getEvents(COMPONENTS.REFINANCE_CALCULATOR),
    REFINANCE_CAL_EVENTS.CALCULATOR_SUMMARY
  );

export const AMORTIZATION = 'amortization';
export const CITY_LIFE = 'cityLife';
export const COST_OF_LIVING = 'costOfLiving';
export const HOME_AFFORDABILITY = 'homeAffordability';
export const HOME_IMPROVEMENT = 'homeImprovements';
export const MORTGAGE = 'mortgage';
export const REFINANCE = 'refinance';
export const RENT_VS_BUY = 'rentVsBuy';

export const CALCULATORS = [
  AMORTIZATION,
  COST_OF_LIVING,
  CITY_LIFE,
  HOME_AFFORDABILITY,
  HOME_IMPROVEMENT,
  MORTGAGE,
  REFINANCE,
  RENT_VS_BUY,
] as const;

export type Calculator = typeof CALCULATORS[number];

export const AMORTIZATION_REDUCER = 'mortgageCalculator';
export const CITY_LIFE_REDUCER = 'cityLife';
export const COST_OF_LIVING_REDUCER = 'costOfLivingCalculator';
export const HOME_AFFORDABILITY_REDUCER = 'results';
export const HOME_IMPROVEMENT_REDUCER = 'hifcCalculator';
export const MORTGAGE_REDUCER = 'mortgageCalculator';
export const REFINANCE_REDUCER = 'refinanceCalculator';
export const RENT_VS_BUY_REDUCER = 'rentvsbuyCalculator';

export const CALCULATORS_REDUCERS = [
  AMORTIZATION_REDUCER,
  CITY_LIFE_REDUCER,
  COST_OF_LIVING_REDUCER,
  HOME_AFFORDABILITY_REDUCER,
  HOME_IMPROVEMENT_REDUCER,
  MORTGAGE_REDUCER,
  REFINANCE_REDUCER,
  RENT_VS_BUY_REDUCER,
] as const;

export type CalculatorReducerName = typeof CALCULATORS_REDUCERS[number];

type CalculatorToReducerNameHash = Record<Calculator, CalculatorReducerName>;

export const CALCULATOR_REDUCER_NAME_HASH: CalculatorToReducerNameHash = {
  [AMORTIZATION]: AMORTIZATION_REDUCER,
  [COST_OF_LIVING]: COST_OF_LIVING_REDUCER,
  [CITY_LIFE]: CITY_LIFE_REDUCER,
  [HOME_AFFORDABILITY]: HOME_AFFORDABILITY_REDUCER,
  [HOME_IMPROVEMENT]: HOME_IMPROVEMENT_REDUCER,
  [MORTGAGE]: MORTGAGE_REDUCER,
  [REFINANCE]: REFINANCE_REDUCER,
  [RENT_VS_BUY]: RENT_VS_BUY_REDUCER,
};
